import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
} from "react"

const PEN_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M3 21l1.65-3.8 2.15 2.15L3 21zm4.39-3.29l-2.1-2.1L16.95 3.95c.39-.39 1.02-.39 1.41 0l.71.71c.39.39.39 1.02 0 1.41L7.39 17.71z' fill='%234CAF50'/%3E%3C/svg%3E") 2 22, auto`

const AnnotationCanvas = forwardRef(
    ({ width, height, color, strokeWidth, enabled, onStrokeEnd, strokesRef: externalStrokesRef }, ref) => {
        const containerRef = useRef(null)
        const canvasRef = useRef(null)
        const internalStrokesRef = useRef([])
        const activeStrokesRef = useRef(externalStrokesRef || internalStrokesRef)
        activeStrokesRef.current = externalStrokesRef || internalStrokesRef
        const currentStrokeRef = useRef(null)
        const isDrawingRef = useRef(false)
        const redrawRef = useRef(null)

        // Props in refs — handlers read these, never stale
        const enabledRef = useRef(enabled)
        const colorRef = useRef(color)
        const strokeWidthRef = useRef(strokeWidth)
        const onStrokeEndRef = useRef(onStrokeEnd)
        enabledRef.current = enabled
        colorRef.current = color
        strokeWidthRef.current = strokeWidth
        onStrokeEndRef.current = onStrokeEnd

        // Create canvas imperatively — completely outside React's rendering lifecycle.
        // React never touches this element: no reconciliation, no attribute diffs, no clearing.
        useEffect(() => {
            const container = containerRef.current
            if (!container) return

            const canvas = document.createElement("canvas")
            canvas.style.position = "absolute"
            canvas.style.top = "0"
            canvas.style.left = "0"
            canvas.style.touchAction = "none"
            canvas.style.zIndex = "10"
            canvas.style.pointerEvents = enabledRef.current ? "auto" : "none"
            canvas.style.cursor = enabledRef.current ? PEN_CURSOR : "default"
            container.appendChild(canvas)
            canvasRef.current = canvas

            const getPoint = (e) => {
                const rect = canvas.getBoundingClientRect()
                return {
                    x: (e.clientX - rect.left) * (canvas.width / rect.width),
                    y: (e.clientY - rect.top) * (canvas.height / rect.height),
                }
            }

            const redraw = () => {
                const ctx = canvas.getContext("2d")
                ctx.clearRect(0, 0, canvas.width, canvas.height)

                for (const stroke of activeStrokesRef.current.current) {
                    if (stroke.points.length < 2) continue
                    ctx.beginPath()
                    ctx.strokeStyle = stroke.color
                    ctx.lineWidth = stroke.strokeWidth
                    ctx.lineCap = "round"
                    ctx.lineJoin = "round"
                    ctx.moveTo(stroke.points[0].x, stroke.points[0].y)
                    for (let i = 1; i < stroke.points.length; i++) {
                        ctx.lineTo(stroke.points[i].x, stroke.points[i].y)
                    }
                    ctx.stroke()
                }

                // Preserve in-progress stroke during redraw (e.g. resize)
                const current = currentStrokeRef.current
                if (current && current.points.length >= 2) {
                    ctx.beginPath()
                    ctx.strokeStyle = current.color
                    ctx.lineWidth = current.strokeWidth
                    ctx.lineCap = "round"
                    ctx.lineJoin = "round"
                    ctx.moveTo(current.points[0].x, current.points[0].y)
                    for (let i = 1; i < current.points.length; i++) {
                        ctx.lineTo(current.points[i].x, current.points[i].y)
                    }
                    ctx.stroke()
                }
            }
            redrawRef.current = redraw

            // --- Pointer handlers (plain functions, no React deps) ---

            const handlePointerDown = (e) => {
                if (!enabledRef.current) return
                e.preventDefault()
                canvas.setPointerCapture(e.pointerId)
                isDrawingRef.current = true
                const point = getPoint(e)
                currentStrokeRef.current = {
                    points: [point],
                    color: colorRef.current,
                    strokeWidth: strokeWidthRef.current,
                }
                const ctx = canvas.getContext("2d")
                ctx.beginPath()
                ctx.strokeStyle = colorRef.current
                ctx.lineWidth = strokeWidthRef.current
                ctx.lineCap = "round"
                ctx.lineJoin = "round"
                ctx.moveTo(point.x, point.y)
                ctx.lineTo(point.x, point.y)
                ctx.stroke()
            }

            const handlePointerMove = (e) => {
                if (!isDrawingRef.current || !currentStrokeRef.current) return
                e.preventDefault()

                // getCoalescedEvents() returns ALL intermediate points the browser
                // coalesced into this single event during a long frame / blocked main thread.
                // This is what eliminates jumps: instead of a straight line from A to Z,
                // we get A→B→C→...→Z for a smooth curve.
                const coalesced = e.getCoalescedEvents?.()
                const events = coalesced && coalesced.length > 0 ? coalesced : [e]

                const ctx = canvas.getContext("2d")
                for (const ce of events) {
                    const point = getPoint(ce)
                    currentStrokeRef.current.points.push(point)

                    const points = currentStrokeRef.current.points
                    const len = points.length
                    if (len >= 2) {
                        ctx.beginPath()
                        ctx.strokeStyle = currentStrokeRef.current.color
                        ctx.lineWidth = currentStrokeRef.current.strokeWidth
                        ctx.lineCap = "round"
                        ctx.lineJoin = "round"
                        ctx.moveTo(points[len - 2].x, points[len - 2].y)
                        ctx.lineTo(points[len - 1].x, points[len - 1].y)
                        ctx.stroke()
                    }
                }
            }

            const handlePointerUp = () => {
                if (!isDrawingRef.current || !currentStrokeRef.current) return
                isDrawingRef.current = false
                activeStrokesRef.current.current.push(currentStrokeRef.current)
                currentStrokeRef.current = null
                onStrokeEndRef.current?.()
            }

            canvas.addEventListener("pointerdown", handlePointerDown)
            canvas.addEventListener("pointermove", handlePointerMove)
            canvas.addEventListener("pointerup", handlePointerUp)
            canvas.addEventListener("pointerleave", handlePointerUp)
            canvas.addEventListener("lostpointercapture", handlePointerUp)

            return () => {
                canvas.removeEventListener("pointerdown", handlePointerDown)
                canvas.removeEventListener("pointermove", handlePointerMove)
                canvas.removeEventListener("pointerup", handlePointerUp)
                canvas.removeEventListener("pointerleave", handlePointerUp)
                canvas.removeEventListener("lostpointercapture", handlePointerUp)
                container.removeChild(canvas)
                canvasRef.current = null
                redrawRef.current = null
            }
        }, [])

        useImperativeHandle(
            ref,
            () => ({
                undo() {
                    activeStrokesRef.current.current.pop()
                    redrawRef.current?.()
                },
                clearAll() {
                    activeStrokesRef.current.current = []
                    redrawRef.current?.()
                },
                redraw() {
                    redrawRef.current?.()
                },
                get strokeCount() {
                    return activeStrokesRef.current.current.length
                },
            }),
            []
        )

        // Handle dimension changes — only thing that touches canvas attributes
        useEffect(() => {
            const canvas = canvasRef.current
            if (!canvas || !width || !height) return
            canvas.width = width
            canvas.height = height
            canvas.style.width = width + "px"
            canvas.style.height = height + "px"
            redrawRef.current?.()
        }, [width, height])

        // Handle enabled/disabled toggle
        useEffect(() => {
            const canvas = canvasRef.current
            if (!canvas) return
            canvas.style.pointerEvents = enabled ? "auto" : "none"
            canvas.style.cursor = enabled ? PEN_CURSOR : "default"
        }, [enabled])

        // Redraw when strokesRef changes (e.g. switching activities)
        useEffect(() => {
            redrawRef.current?.()
        }, [externalStrokesRef])

        // Container div — React only manages this wrapper, never the canvas
        return React.createElement("div", {
            ref: containerRef,
            style: {
                position: "absolute",
                top: 0,
                left: 0,
                width,
                height,
                pointerEvents: "none",
                zIndex: 10,
            },
        })
    }
)

AnnotationCanvas.displayName = "AnnotationCanvas"

export default AnnotationCanvas
